import {
  Camera,
  Carrot,
  CircleMinus,
  CirclePlus,
  Flame,
  Snowflake,
  Triangle,
  type LucideIcon,
} from "lucide-react";
import type { PizzaEvaluationMetadata } from "@/api/custom-schema";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { pizzaVerdict, type VerdictCheckKey } from "@/lib/pizza-verdict";
import { cn } from "@/lib/utils";

/** labels are translation keys, translated when rendered */
const SLOTS: { key: VerdictCheckKey; label: TranslationKey; icon: LucideIcon }[] = [
  { key: "photo", label: "list.checkStrip.photo", icon: Camera },
  { key: "base", label: "list.checkStrip.base", icon: Carrot },
  { key: "extras", label: "list.checkStrip.extras", icon: CirclePlus },
  { key: "removals", label: "list.checkStrip.removals", icon: CircleMinus },
  { key: "burnt", label: "list.checkStrip.burnt", icon: Flame },
  { key: "raw", label: "list.checkStrip.raw", icon: Snowflake },
  { key: "saver", label: "list.checkStrip.saver", icon: Triangle },
];

const SIZES = {
  md: { slot: "h-7 w-7", icon: "h-4 w-4", gap: "gap-1" },
  sm: { slot: "h-6 w-6", icon: "h-3.5 w-3.5", gap: "gap-0.5" },
};

/** Column header of the strip: the same icons, so every slot is labelled. */
export function CheckStripHeader({ size = "md" }: { size?: keyof typeof SIZES }) {
  const { t } = useLocalization();
  const sz = SIZES[size];
  return (
    <div className={cn("flex items-center", sz.gap)} aria-label={t("list.checkStrip.label")}>
      {SLOTS.map(({ key, label, icon: Icon }) => (
        <span
          key={key}
          title={t(label)}
          className={cn("flex items-center justify-center text-muted-foreground/70", sz.slot)}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
      ))}
    </div>
  );
}

/**
 * The checks of a verdict as a row of small icons in fixed slots (photo, ingredients, extras, removals,
 * burnt, uncooked, pizza saver): passed in soft green, failed in red, not evaluated faint. Aligned slots let
 * the eye run down a column of the list.
 */
export function CheckStrip({
  metadata,
  size = "md",
  className,
}: {
  metadata?: PizzaEvaluationMetadata | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const { t } = useLocalization();
  const verdict = pizzaVerdict(metadata);
  if (verdict.kind === "not_evaluated") return null;
  const sz = SIZES[size];
  const statusText = (status: string) =>
    status === "N/A"
      ? t("list.checkStrip.notEvaluated")
      : status === "OK"
        ? t("status.ok")
        : status === "NOK"
          ? t("status.nok")
          : status;
  return (
    <div className={cn("flex items-center", sz.gap, className)}>
      {SLOTS.map(({ key, label, icon: Icon }) => {
        const check = verdict.checks.find((c) => c.key === key);
        const status = check?.status ?? "N/A";
        return (
          <span
            key={key}
            title={`${t(label)}: ${statusText(status)}${check?.reason ? ` – ${check.reason}` : ""}`}
            className={cn(
              "flex items-center justify-center rounded-full",
              sz.slot,
              status === "NOK" && "bg-red-500/15 text-red-400 ring-1 ring-inset ring-red-500/40",
              status === "OK" && "bg-green-500/[0.07] text-green-500/80",
              status === "N/A" && "text-muted-foreground/20",
            )}
          >
            <Icon className={sz.icon} strokeWidth={status === "NOK" ? 2.5 : 2} />
          </span>
        );
      })}
    </div>
  );
}
